import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Hotel } from "@/types/hotel";
import {
  useHotelActivityLimit,
  useUpdateHotelActivityLimit,
} from "@/components/pages/admin-partners/hooks/partner-providers.hooks";

interface ActivityLimitFormProps {
  useOverride: boolean;
  onUseOverrideChange: (value: boolean) => void;
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  limit: number;
  onLimitChange: (value: number) => void;
  defaultMaxActivities: number;
  effectiveMaxActivitiesEnabled: boolean;
  effectiveMaxActivities: number;
}

const ActivityLimitForm: React.FC<ActivityLimitFormProps> = ({
  useOverride,
  onUseOverrideChange,
  enabled,
  onEnabledChange,
  limit,
  onLimitChange,
  defaultMaxActivities,
  effectiveMaxActivitiesEnabled,
  effectiveMaxActivities,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Switch
          id="hotel-activity-limit-use-default"
          checked={!useOverride}
          onCheckedChange={(checked) => onUseOverrideChange(!checked)}
        />
        <Label htmlFor="hotel-activity-limit-use-default" className="text-md">
          {t("partners.admin.hotelActivityLimit.useDefault")}
        </Label>
      </div>
      {!useOverride && (
        <p className="text-muted-foreground text-xs">
          {t("partners.admin.hotelActivityLimit.useDefaultHint", {
            count: defaultMaxActivities,
          })}
        </p>
      )}

      {useOverride && (
        <>
          <div className="flex items-center gap-2">
            <Switch
              id="hotel-activity-limit-enabled"
              checked={enabled}
              onCheckedChange={onEnabledChange}
            />
            <Label htmlFor="hotel-activity-limit-enabled" className="text-md">
              {t("partners.admin.hotelActivityLimit.maxActivitiesEnabled")}
            </Label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-activity-limit-value">
              {t("partners.admin.hotelActivityLimit.maxActivities")}
            </Label>
            <Input
              id="hotel-activity-limit-value"
              type="number"
              min={1}
              max={1000}
              disabled={!enabled}
              value={limit}
              onChange={(event) => onLimitChange(Number(event.target.value))}
            />
          </div>
        </>
      )}

      <p className="text-muted-foreground text-sm">
        {effectiveMaxActivitiesEnabled
          ? t("partners.admin.hotelActivityLimit.effectiveLimit", {
              count: effectiveMaxActivities,
            })
          : t("partners.admin.hotelActivityLimit.effectiveNoLimit")}
      </p>
    </div>
  );
};

interface HotelPartnerActivityLimitDialogProps {
  hotel: Hotel | null;
  canManage?: boolean;
  onClose: () => void;
}

export const HotelPartnerActivityLimitDialog: React.FC<
  HotelPartnerActivityLimitDialogProps
> = ({ hotel, canManage = true, onClose }) => {
  const { t } = useTranslation();
  const open = !!hotel && canManage;
  const hotelId = hotel?.id ?? null;
  const tenantId = hotel?.tenantId ?? null;

  const { data, isLoading, isError } = useHotelActivityLimit(
    open ? hotelId : null,
    open ? tenantId : null,
  );
  const updateLimit = useUpdateHotelActivityLimit(hotelId, tenantId);

  const [useOverride, setUseOverride] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    if (!data) return;
    setUseOverride(data.maxActivitiesEnabled !== null);
    setEnabled(data.maxActivitiesEnabled ?? true);
    setLimit(data.maxActivities ?? data.defaultMaxActivities);
  }, [data]);

  const handleSave = () => {
    updateLimit.mutate(
      {
        maxActivitiesEnabled: useOverride ? enabled : null,
        maxActivities: useOverride ? limit : null,
      },
      {
        onSuccess: () => {
          toast.success(t("partners.admin.hotelActivityLimit.saveSuccess"));
          onClose();
        },
        onError: () => {
          toast.error(t("partners.admin.hotelActivityLimit.saveError"));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("partners.admin.hotelActivityLimit.title")}
          </DialogTitle>
          <DialogDescription>
            {t("partners.admin.hotelActivityLimit.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.hotelActivityLimit.loading")}
          </p>
        )}

        {!isLoading && isError && (
          <p className="text-destructive text-sm">
            {t("partners.admin.hotelActivityLimit.loadError")}
          </p>
        )}

        {!isLoading && !isError && data && (
          <ActivityLimitForm
            useOverride={useOverride}
            onUseOverrideChange={setUseOverride}
            enabled={enabled}
            onEnabledChange={setEnabled}
            limit={limit}
            onLimitChange={setLimit}
            defaultMaxActivities={data.defaultMaxActivities}
            effectiveMaxActivitiesEnabled={data.effectiveMaxActivitiesEnabled}
            effectiveMaxActivities={data.effectiveMaxActivities}
          />
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateLimit.isPending}
          >
            {t("partners.admin.hotelActivityLimit.close")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isLoading || isError || !data || updateLimit.isPending}
          >
            {updateLimit.isPending
              ? t("partners.admin.hotelActivityLimit.saving")
              : t("partners.admin.hotelActivityLimit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
